import { useQuery } from '@tanstack/react-query';
import { api } from '@/api/client';
import { cn } from '@/lib/utils';

/**
 * Client logos (server/companyLogos.ts): the round 720 × 720 PNGs from the Marketing SharePoint folder, matched to
 * Autotask companies via IT Glue or Admin › Client mappings › Client logos. One list (`companyId → url`, the URL
 * carries the logo's hash so the browser caches the image for good) for every place that shows one.
 */
export function useCompanyLogos(enabled = true) {
  return useQuery({
    enabled,
    queryKey: ['companies', 'logos'],
    queryFn: () => api.get<{ logos: Record<string, string> }>('/api/companies/logos'),
    staleTime: 10 * 60_000,
  });
}

/** A company's logo URL, or null (none, or the list isn't loaded yet). */
export function useCompanyLogoUrl(companyId: number | null | undefined, enabled = true): string | null {
  const q = useCompanyLogos(enabled && companyId != null);
  return companyId != null ? q.data?.logos[String(companyId)] ?? null : null;
}

/** The logo as a circle (the PNGs are already round on transparency). `fallback` shows when there's none. */
export function CompanyLogo({ url, size = 20, className, fallback = null, title }: {
  url: string | null; size?: number; className?: string; fallback?: React.ReactNode; title?: string;
}) {
  if (!url) return <>{fallback}</>;
  return (
    <img src={url} alt="" title={title} width={size} height={size} loading="lazy" decoding="async"
      className={cn('shrink-0 rounded-full object-contain', className)} style={{ width: size, height: size }} />
  );
}
